import React, { useState, useRef, ReactNode, useEffect } from 'react'
import styled, { CSSProperties } from 'styled-components'

import { Stylable } from 'utils/types'

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left'

interface TooltipProps extends Stylable {
  children: ReactNode
  content: ReactNode
  position?: TooltipPosition
  delay?: number
  tooltipBoxStyles?: CSSProperties
}

const TooltipWrapper = styled.div`
  display: inline-block;
  position: relative;
`

interface TooltipBoxProps {
  position: TooltipPosition
  visible: boolean
}

const TooltipBox = styled.div<TooltipBoxProps>`
  position: absolute;
  color: ${p => p.theme.colors.text.primaryDarkBackground};
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
  background-color: ${p => p.theme.colors.button.background};
  padding: 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  max-width: 500px;
  min-width: 400px;
  white-space: normal;
  word-wrap: break-word;
  box-sizing: border-box; /* Ensures padding is included in the width */
  ${({ position }) =>
    position === 'top' &&
    `
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      margin-bottom: 8px;
  `}

  ${({ position }) =>
    position === 'right' &&
    `
      top: 50%;
      left: 100%;
      transform: translateY(-50%);
      margin-left: 8px;
  `}

  ${({ position }) =>
    position === 'bottom' &&
    `
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      margin-top: 8px;
  `}

  ${({ position }) =>
    position === 'left' &&
    `
      top: 50%;
      right: 100%;
      transform: translateY(-50%);
      margin-right: 8px;
  `};

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    min-width: 250px;
  }
`

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  delay = 300,
  tooltipBoxStyles,
  style,
}) => {
  const [visible, setVisible] = useState(false)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const showTooltip = () => {
    const id = setTimeout(() => {
      setVisible(true)
    }, delay)
    setTimeoutId(id)
  }

  const hideTooltip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    setVisible(false)
  }

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [timeoutId])

  return (
    <TooltipWrapper onMouseEnter={showTooltip} onMouseLeave={hideTooltip} style={style}>
      {children}
      {visible && (
        <TooltipBox ref={tooltipRef} visible={visible} position={position} style={tooltipBoxStyles}>
          {content}
        </TooltipBox>
      )}
    </TooltipWrapper>
  )
}
